import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Alert, ListGroup, Row, Col, Badge } from "react-bootstrap";
import { FaSearch, FaCamera, FaUsers, FaChalkboardTeacher, FaCrown, FaTrash } from "react-icons/fa";
import {
  getTeachersBySchoolId,
  getClassesByTeacherId,
  removeFromSharedClass,
  removeOrReplaceClassOwner,
  replaceClassCreator
} from "../services/APIClient"; // Adjusted API call import

function RemoveTeacherFromClass({ show, onHide, schoolid }) {
  const [teachers, setTeachers] = useState([]); // Store teachers
  const [selectedTeacher, setSelectedTeacher] = useState(null); // Store the selected teacher
  const [classes, setClasses] = useState([]); // Store the classes that the selected teacher is part of
  const [statusMessage, setStatusMessage] = useState(""); // Status message for success/failure
  const [alertVariant, setAlertVariant] = useState(""); // Alert variant for Bootstrap

  // Filter states
  const [classGradeFilter, setClassGradeFilter] = useState("all");
  const [classAcademicYearFilter, setClassAcademicYearFilter] = useState("all");
  const [classSnapshotFilter, setClassSnapshotFilter] = useState("all");
  const [classSearchTerm, setClassSearchTerm] = useState("");

  const [showConfirmationModal, setShowConfirmationModal] = useState(false); // For the confirmation modal
  const [selectedClassForAction, setSelectedClassForAction] = useState(null); // The class selected for action
  const [newOwnerOrCreator, setNewOwnerOrCreator] = useState(""); // For assigning a new owner/creator if needed

  // Available grade levels using boolean system
  const gradeLevels = [
    { value: "all", label: "All Grades" },
    { value: "grade_level1", label: "Junior Infants" },
    { value: "grade_level2", label: "Senior Infants" },
    { value: "grade_level3", label: "1st Class" },
    { value: "grade_level4", label: "2nd Class" },
    { value: "grade_level5", label: "3rd Class" },
    { value: "grade_level6", label: "4th Class" },
    { value: "grade_level7", label: "5th Class" },
    { value: "grade_level8", label: "6th Class" }
  ];

  // Academic years - will be populated from actual data
  const [availableAcademicYears, setAvailableAcademicYears] = useState([
    { value: "all", label: "All Academic Years" }
  ]);

  // Snapshot filter options
  const snapshotOptions = [
    { value: "all", label: "All Classes" },
    { value: "active", label: "Active Classes Only" },
    { value: "snapshot", label: "Snapshots Only" }
  ];

  const getGradeLabelFromClass = (classItem) => {
    if (classItem.grade_level1) return "Junior Infants";
    if (classItem.grade_level2) return "Senior Infants";
    if (classItem.grade_level3) return "1st Class";
    if (classItem.grade_level4) return "2nd Class";
    if (classItem.grade_level5) return "3rd Class";
    if (classItem.grade_level6) return "4th Class";
    if (classItem.grade_level7) return "5th Class";
    if (classItem.grade_level8) return "6th Class";
    return "Unknown Grade";
  };

  const getGradeBooleanFromClass = (classItem) => {
    if (classItem.grade_level1) return "grade_level1";
    if (classItem.grade_level2) return "grade_level2";
    if (classItem.grade_level3) return "grade_level3";
    if (classItem.grade_level4) return "grade_level4";
    if (classItem.grade_level5) return "grade_level5";
    if (classItem.grade_level6) return "grade_level6";
    if (classItem.grade_level7) return "grade_level7";
    if (classItem.grade_level8) return "grade_level8";
    return "unknown";
  };

  // Fetch teachers when the modal opens
  useEffect(() => {
    if (schoolid) {
      getTeachersBySchoolId(schoolid)
        .then((response) => {
          setTeachers(response); // Store the teachers
        })
        .catch((error) => {
          setStatusMessage("Error fetching teachers. Please try again.");
          setAlertVariant("danger");
        });
    }
  }, [schoolid]);

  // Fetch classes when a teacher is selected
  useEffect(() => {
    if (selectedTeacher) {
      getClassesByTeacherId(selectedTeacher)
        .then((response) => {
          const mergedClasses = mergeClasses(response);
          setClasses(mergedClasses); // Store the merged classes
        })
        .catch((error) => {
          setStatusMessage("Error fetching classes for the teacher. Please try again.");
          setAlertVariant("danger");
        });
    }
  }, [selectedTeacher]);

  // Helper function to merge duplicate classes and combine associations
  const mergeClasses = (classData) => {
    const classMap = {};

    classData.forEach(({ class: classItem, association }) => {
      const classId = classItem.id;
      const gradeLabel = getGradeLabelFromClass(classItem);
      const gradeBoolean = getGradeBooleanFromClass(classItem);

      if (classMap[classId]) {
        classMap[classId].association += `, ${association}`;
      } else {
        classMap[classId] = {
          ...classItem,
          id: classId,
          association,
          grade: gradeLabel,
          gradeBoolean: gradeBoolean,
          academicYear: classItem.academicYear || null,
          snapshot: classItem.snapshot || false,
          studentNumber: classItem.studentNumber || 0
        };
      }
    });

    const transformedClasses = Object.values(classMap);

    // Extract unique academic years for filter options
    const uniqueAcademicYears = [...new Set(transformedClasses
      .map(cls => cls.academicYear)
      .filter(year => year) // Remove null/undefined
    )].sort().reverse(); // Sort newest first

    const academicYearOptions = [
      { value: "all", label: "All Academic Years" },
      ...uniqueAcademicYears.map(year => ({
        value: year,
        label: `${year} Academic Year`
      }))
    ];

    setAvailableAcademicYears(academicYearOptions);

    return transformedClasses;
  };

  // Filter classes based on current filter settings
  const getFilteredClasses = () => {
    return classes.filter(classItem => {
      const matchesGrade = classGradeFilter === "all" || classItem.gradeBoolean === classGradeFilter;
      const matchesAcademicYear = classAcademicYearFilter === "all" || classItem.academicYear === classAcademicYearFilter;

      // Snapshot filtering logic
      let matchesSnapshot = true;
      if (classSnapshotFilter === "active") {
        matchesSnapshot = !classItem.snapshot;
      } else if (classSnapshotFilter === "snapshot") {
        matchesSnapshot = classItem.snapshot;
      }

      const matchesSearch = classItem.name?.toLowerCase().includes(classSearchTerm.toLowerCase()) ||
        classItem.association?.toLowerCase().includes(classSearchTerm.toLowerCase());
      return matchesGrade && matchesAcademicYear && matchesSnapshot && matchesSearch;
    });
  };

  // Handle the red X click (show confirmation modal)
  const handleDeleteClassAssociation = (classItem) => {
    setSelectedClassForAction(classItem); // Set the class for action
    setShowConfirmationModal(true); // Show confirmation modal
  };

  // Handle confirmed deletion/removal based on association
  const confirmClassAction = () => {
    const { association } = selectedClassForAction;

    // 1. Handle the shared class association removal (if applicable)
    const sharedPromise = association.includes("shared")
      ? removeFromSharedClass(selectedClassForAction.id, selectedTeacher)
        .then(() => {
          setStatusMessage("Shared association removed successfully.");
          setAlertVariant("success");
        })
        .catch(() => {
          setStatusMessage("Failed to remove shared association.");
          setAlertVariant("danger");
        })
      : Promise.resolve();  // No shared association to remove

    // 2. Handle owner removal and replacement (if applicable)
    const ownerPromise = association.includes("owned")
      ? sharedPromise.then(() => {
        return removeOrReplaceClassOwner(selectedClassForAction.id, newOwnerOrCreator || "")
          .then(() => {
            setStatusMessage("Class owner changed successfully.");
            setAlertVariant("success");
          })
          .catch(() => {
            setStatusMessage("Failed to change class owner.");
            setAlertVariant("danger");
          });
      })
      : sharedPromise;  // Continue if there's no owner association

    // 3. Handle class creator replacement (if applicable)
    const creatorPromise = association.includes("created")
      ? ownerPromise.then(() => {
        return replaceClassCreator(selectedClassForAction.id, newOwnerOrCreator)
          .then(() => {
            setStatusMessage("Class creator replaced successfully.");
            setAlertVariant("success");
          })
          .catch(() => {
            setStatusMessage("Failed to replace class creator.");
            setAlertVariant("danger");
          });
      })
      : ownerPromise;  // Continue if there's no creator association

    // 4. Execute all promises in sequence
    creatorPromise.finally(() => {
      setClasses(classes.filter((cls) => cls.id !== selectedClassForAction.id));  // Update UI
      setShowConfirmationModal(false);
      setNewOwnerOrCreator(""); // Clear owner/creator selection
    });
  };

  const handleClose = () => {
    setSelectedTeacher(null);
    setClasses([]);
    setStatusMessage("");
    setClassGradeFilter("all");
    setClassAcademicYearFilter("all");
    setClassSnapshotFilter("all");
    setClassSearchTerm("");
    onHide();
  };

  const getAssociationBadges = (association) => {
    const badges = [];
    if (association.includes("created")) {
      badges.push(<Badge key="creator" bg="primary" className="me-1">Creator</Badge>);
    }
    if (association.includes("owned")) {
      badges.push(<Badge key="owner" bg="warning" className="me-1">Owner</Badge>);
    }
    if (association.includes("shared")) {
      badges.push(<Badge key="shared" bg="info" className="me-1">Shared</Badge>);
    }
    return badges;
  };

  const filteredClasses = getFilteredClasses();
  const selectedTeacherName = teachers.find(t => t.id === selectedTeacher)?.name;

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        size="lg"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaTrash className="me-2 text-danger" />
            Remove Teacher from Classes
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ minHeight: '500px' }}>
          {/* Dropdown for selecting a teacher */}
          <Form.Group className="mb-4">
            <Form.Label className="fw-medium">Select Teacher to Remove:</Form.Label>
            <Form.Control
              as="select"
              value={selectedTeacher || ""}
              onChange={(e) => setSelectedTeacher(e.target.value)}
            >
              <option value="">Select Teacher</option>
              {teachers.map((teacher) => (
                <option key={teacher.id} value={teacher.id}>
                  {teacher.name}
                </option>
              ))}
            </Form.Control>
          </Form.Group>

          {/* Filters - only show when teacher is selected */}
          {selectedTeacher && classes.length > 0 && (
            <>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">
                  Classes Associated with {selectedTeacherName}
                  <Badge bg="secondary" className="ms-2">{filteredClasses.length} of {classes.length}</Badge>
                </h5>
              </div>

              {/* Class Filters */}
              <Row className="mb-3">
                <Col md={3}>
                  <Form.Select
                    value={classAcademicYearFilter}
                    onChange={(e) => setClassAcademicYearFilter(e.target.value)}
                    size="sm"
                  >
                    {availableAcademicYears.map(year => (
                      <option key={year.value} value={year.value}>
                        {year.label}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={3}>
                  <Form.Select
                    value={classGradeFilter}
                    onChange={(e) => setClassGradeFilter(e.target.value)}
                    size="sm"
                  >
                    {gradeLevels.map(grade => (
                      <option key={grade.value} value={grade.value}>
                        {grade.label}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={3}>
                  <Form.Select
                    value={classSnapshotFilter}
                    onChange={(e) => setClassSnapshotFilter(e.target.value)}
                    size="sm"
                  >
                    {snapshotOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={3}>
                  <div className="position-relative">
                    <Form.Control
                      type="text"
                      placeholder="Search classes..."
                      value={classSearchTerm}
                      onChange={(e) => setClassSearchTerm(e.target.value)}
                      size="sm"
                    />
                    <FaSearch className="position-absolute top-50 end-0 translate-middle-y me-2 text-muted" />
                  </div>
                </Col>
              </Row>
            </>
          )}

          {/* List of classes the selected teacher is part of */}
          {selectedTeacher && (
            <div style={{ maxHeight: "350px", overflowY: "auto" }}>
              {filteredClasses.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  {classes.length === 0 ? "No classes found for this teacher" : "No classes match the current filters"}
                </div>
              ) : (
                <ListGroup>
                  {filteredClasses.map((classItem) => (
                    <ListGroup.Item
                      key={classItem.id}
                      className="d-flex justify-content-between align-items-start"
                    >
                      <div className="flex-grow-1">
                        <div className="d-flex align-items-center mb-2">
                          <div className="fw-medium me-2">{classItem.name}</div>
                          {classItem.snapshot && (
                            <Badge bg="secondary" className="me-2">
                              <FaCamera className="me-1" size={10} />
                              Snapshot
                            </Badge>
                          )}
                          {classItem.trial_class && (
                            <Badge bg="warning" className="me-2">Trial</Badge>
                          )}
                        </div>

                        <div className="small text-muted mb-2">
                          <div>
                            <strong>Grade:</strong> {classItem.grade}
                            {classItem.academicYear && ` • ${classItem.academicYear}`}
                            {!classItem.snapshot && classItem.studentNumber !== undefined && (
                              <Badge bg="light" text="dark" className="ms-2">
                                <FaUsers className="me-1" size={10} />
                                {classItem.studentNumber} student{classItem.studentNumber !== 1 ? 's' : ''}
                              </Badge>
                            )}
                          </div>
                        </div>

                        <div className="mt-2">
                          <strong className="small text-muted me-2">Associations:</strong>
                          {getAssociationBadges(classItem.association)}
                        </div>
                      </div>

                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleDeleteClassAssociation(classItem)}
                        className="ms-3"
                      >
                        <FaTrash />
                      </Button>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </div>
          )}

          {/* Status message for success or failure */}
          {statusMessage && (
            <Alert variant={alertVariant} className="mt-3" dismissible onClose={() => setStatusMessage("")}>
              {statusMessage}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex justify-content-between w-100 align-items-center">
            <div className="text-muted">
              {selectedTeacher && filteredClasses.length > 0 && (
                <span>
                  Managing {filteredClasses.length} class association{filteredClasses.length !== 1 ? 's' : ''}
                  for {selectedTeacherName}
                </span>
              )}
            </div>
            <Button variant="secondary" onClick={handleClose}>Close</Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Confirmation Modal */}
      {selectedClassForAction && (
        <Modal show={showConfirmationModal} onHide={() => setShowConfirmationModal(false)} centered size="lg">
          <Modal.Header closeButton className="bg-danger text-white">
            <Modal.Title>
              <FaTrash className="me-2" />
              Confirm Class Association Removal
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <div className="text-center mb-4">
              <div className="bg-danger-light rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                style={{ width: '60px', height: '60px' }}>
                <FaTrash className="text-danger" size={24} />
              </div>
              <h5 className="mb-2">Remove Teacher from Class</h5>
            </div>

            <div className="mb-4">
              <div className="border rounded-3 p-3 bg-light">
                <div className="d-flex align-items-center mb-2">
                  <strong>Teacher:</strong>
                  <span className="ms-2">{selectedTeacherName}</span>
                </div>
                <div className="d-flex align-items-center mb-2">
                  <strong>Class:</strong>
                  <span className="ms-2 me-2">{selectedClassForAction.name}</span>
                  {selectedClassForAction.snapshot && (
                    <Badge bg="secondary">
                      <FaCamera className="me-1" size={10} />
                      Snapshot
                    </Badge>
                  )}
                </div>
                <div className="d-flex align-items-center mb-2">
                  <strong>Current Associations:</strong>
                  <span className="ms-2">{getAssociationBadges(selectedClassForAction.association)}</span>
                </div>
              </div>
            </div>

            <p className="mb-3">
              Are you sure you want to remove <strong>{selectedTeacherName}</strong> from
              the class <strong>{selectedClassForAction.name}</strong>?
            </p>

            {(selectedClassForAction.association.includes("owned") || selectedClassForAction.association.includes("created")) && (
              <div className="alert alert-warning">
                <strong>Important:</strong> This teacher has special roles in this class that require replacement.
              </div>
            )}

            {selectedClassForAction.association.includes("owned") && (
              <div className="mb-3">
                <p><strong>This teacher is the class owner.</strong> Please assign a new owner:</p>
                <Form.Group>
                  <Form.Control
                    as="select"
                    value={newOwnerOrCreator || ""}
                    onChange={(e) => setNewOwnerOrCreator(e.target.value)}
                  >
                    <option value="">Select New Owner</option>
                    {teachers.filter(t => t.id !== selectedTeacher).map((teacher) => (
                      <option key={teacher.id} value={teacher.id}>
                        {teacher.name}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </div>
            )}

            {selectedClassForAction.association.includes("created") && (
              <div className="mb-3">
                <p><strong>This teacher created the class.</strong> You must assign a new teacher:</p>
                <Form.Group>
                  <Form.Control
                    as="select"
                    value={newOwnerOrCreator || ""}
                    onChange={(e) => setNewOwnerOrCreator(e.target.value)}
                  >
                    <option value="">Select New Teacher</option>
                    {teachers.filter(t => t.id !== selectedTeacher).map((teacher) => (
                      <option key={teacher.id} value={teacher.id}>
                        {teacher.name}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowConfirmationModal(false)}>Cancel</Button>
            <Button
              variant="danger"
              onClick={confirmClassAction}
              disabled={(selectedClassForAction.association.includes("owned") || selectedClassForAction.association.includes("created")) && !newOwnerOrCreator}
            >
              Confirm Removal
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
}

export default RemoveTeacherFromClass;